import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Play, ChevronRight, Crown, Sparkles, Flag, NotebookPen, ArrowRight, FileText, BookOpen, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { VipSwitcher } from './CoachingPage';
import StabilityBallProgressions, { SECTIONS as ballSections } from '../components/StabilityBallProgressions';
import MyMovements from '../components/MyMovements';
import { VipSearchBar, VipSearchResults, SearchChips, matchItems } from '../components/VipSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEARCH_CHIPS = ['Hip', 'Spine', 'Back', 'Release', 'Fascia', 'Stress', 'Walk', 'Feet'];

// ═══════════════════════════════════════════════
// VIP LIBRARY — 4 categories, placeholder videos
// Each video accepts: title, duration, videoUrl (YouTube), description
// ═══════════════════════════════════════════════
export const categories = [
  {
    id: 'postural-development',
    title: 'Postural Development',
    tagline: 'Build alignment, awareness, and lasting structural support.',
    accent: '#7a6a4a',
    accentBg: 'bg-[#efe8d8]',
    dualVideo: true, // each square opens to a full Tutorial + a Quick version
    videos: [
      {
        title: 'Infant / Postural Development',
        duration: '',
        description: "The main 4 Postural Development exercises to integrate naval, spine, pelvis, shoulder, head, and legs.\n\nDo each exercise for 1 minute.\n\n1. Rolling — Keep eyes on toes. 4 levels of difficulty: (1) hold shins, (2) hold ankles, (3) hold feet (each foot separately), (4) hold both feet with interlaced fingers.\n\n2. Toe to Nose — Order matters most; intention is everything. Eyes move first (toward toes), head moves next (you can now see toes), foot moves last, then bring toes toward nose. Alternate sides to mimic walking.\n\n3. Wiggle Up — Like crawling on the shoulder-blade points and the pelvis points.\n\n4. Wiggle Down — Like a caterpillar, moving from head to tail in a wave motion.",
        videoUrl: 'https://youtu.be/uU5F1mShyKo',
      },
      {
        title: 'Infant / Postural Development Cheat Sheet',
        duration: '',
        description: 'A quick-reference walkthrough of the Infant / Postural Development series — the cues and key points at a glance.',
        videoUrl: 'https://youtu.be/6wVY7yWewXU',
      },
      {
        title: 'The Greatest Stretch Ever',
        duration: '',
        description: 'A deep quad and fascial-line release. By opening the front of the body, we naturally settle back into the postural (back) body — undoing the forward pull of daily life and restoring easy, upright alignment.',
        videoUrl: 'https://youtu.be/GXDGThl3yK4',
      },
      {
        title: 'The Greatest Stretch Ever — Cheat Sheet',
        duration: '',
        description: 'The accompanying quick-reference for The Greatest Stretch Ever — cues and key points at a glance.',
        videoUrl: 'https://youtu.be/2DMTbKIqSJg',
      },
      {
        title: 'Fascial Flossing — Bringing Your Tigger Back (Bounce)',
        duration: '',
        description: 'Gentle, springy bounce work to re-hydrate and re-energise the fascia — restoring the natural elastic bounce in your body.',
        videoUrl: 'https://youtu.be/bKWb7Yrm9no',
      },
      { title: 'Hip Development — Leader / Follower Inner Thigh', duration: '', description: 'Leader / follower inner-thigh work to develop the hips.', videoUrl: 'https://youtu.be/lN5_pvkuo68' },
      { title: 'Hip Development — Hip Thrust / Glute Med Work', duration: '', description: 'Hip thrust and glute-medius work to strengthen and stabilise the hips.', videoUrl: 'https://youtu.be/FlJdIAkrl0A' },
      { title: 'Diagonals — Up and Over', duration: '', description: 'Diagonal "up and over" patterning to connect and integrate the body across the midline.', videoUrl: 'https://youtu.be/l9mYPJfI_IU' },
      { title: 'Spinal Connection — Dead Bug Series', duration: '', description: 'Coming soon.' },
      { title: 'Spinal Connection — Cobra Series', duration: '', description: 'Coming soon.' },
      { title: 'Movement Development — Getting Up', duration: '', description: 'Coming soon.' },
      {
        title: 'Organ Massage / Release',
        duration: '',
        description: 'A gentle organ massage and release practice to soften, mobilise and bring circulation to the deeper structures of the body.',
        videoUrl: 'https://youtu.be/SjkAbNGk7e4',
      },
    ],
  },
  {
    id: 'putting-it-together',
    title: 'Putting It Together',
    tagline: 'Linking the pieces into one connected, whole-body flow.',
    accent: '#8a6a5a',
    accentBg: 'bg-[#f1e6dc]',
    videos: [
      {
        title: 'From the Bottom to the Top',
        duration: '',
        description: 'Bringing the postural development pieces together — moving from the feet and pelvis up through the spine to the head as one connected whole.',
        videoUrl: 'https://youtu.be/mNQjBlhzOEA',
      },
    ],
  },
  {
    id: 'multifidus-ql',
    title: 'Multifidus / QL Conditioning',
    tagline: 'Reconditioning the deep postural system — multifidus, QL, and spinal stabilizers.',
    accent: '#6a7a5a',
    accentBg: 'bg-[#e3e8da]',
    videos: [
      {
        title: 'Hanna 3 Part Flexion',
        duration: '',
        description: 'Connecting legs to pelvis / spine, head to pelvis / spine, and head to legs through the pelvis and spine. Also considered multifidus conditioning and reconditioning — part of our deep postural system. See The Back Reset Program for a deeper look at reconditioning the back.',
        videoUrl: 'https://youtu.be/ePjCDq03Zqg',
      },
      {
        title: 'Hanna 3 Part Flexion — Cheat Sheet',
        duration: '',
        description: 'Quick-reference walkthrough of the Hanna 3 Part Flexion — cues and key points at a glance.',
        videoUrl: 'https://youtu.be/FF_7TLIcVFQ',
      },
      {
        title: 'The Fish',
        duration: '',
        description: 'Multifidus / QL conditioning through "The Fish" — waking up the deep spinal stabilisers.',
        videoUrl: 'https://youtu.be/2Gjz_F02PIA',
      },
      {
        title: 'Mouse Hole',
        duration: '',
        description: 'Multifidus / QL coordination with "Mouse Hole" — refining deep postural control.',
        videoUrl: 'https://youtu.be/qU_A95ZiIVA',
      },
      { title: 'Back Reset I', duration: '', description: 'Coming soon.' },
      { title: 'Neck Reset I', duration: '', description: 'Coming soon.' },
      { title: 'Tummy Time Series', duration: '', description: 'Coming soon.' },
      { title: 'Fine Tuning', duration: '', description: 'Coming soon.' },
    ],
  },
  {
    id: 'restorative',
    title: 'Restorative',
    tagline: 'Slow, soothing routines to downshift the nervous system.',
    accent: '#6a5a78',
    accentBg: 'bg-[#efeaf0]',
    videos: [
      {
        title: 'Restorative Movement 1',
        duration: '',
        description: 'Great for evening / night time — a gentle sleep invitation. More details in the video.',
        videoUrl: 'https://youtu.be/yCVIIdBWptI',
      },
      {
        title: 'Restorative Movement 2',
        duration: '',
        description: 'More of an AM practice — only do the more vigorous aspects if your body feels ready.',
        videoUrl: 'https://youtu.be/9XoWX9wr6ew',
      },
      {
        title: 'Restorative Movement 3',
        duration: '',
        description: 'RM2 plus QiGong — a longer flow that layers gentle Qi Gong onto the morning practice.',
        videoUrl: 'https://youtu.be/rSrK-FKMeFs',
      },
    ],
  },
  {
    id: 'stability-ball',
    title: 'Stability Ball',
    tagline: 'Build balance, core control, and whole-body coordination on the ball.',
    accent: '#5a7a6a',
    accentBg: 'bg-[#e4efe8]',
    videos: [
      {
        title: 'Spinal Movements and the Ball',
        duration: '',
        videoUrl: 'https://youtu.be/RQj9rbxuos4',
        description: 'An orientation to the ball — and, more so, an easy, accessible way to reach our deeper, subtle postural system. A lovely way to loosen up after work or exercise, and a more mindless way of just moving the body (and repatterning at the same time).',
        content: `If you have a lot of back pain — or any pain doing these — please let me know.

So many exercises ask for a lot of mindfulness; this one is the opposite. Let it be easy and free.

EXPLORE (back and forth, in no particular order):
• Hula hoops
• Figure 8s
• Bouncing

Try each one two ways:
• A "loose and free" version (mobility)
• A more muscular, activated version

PROGRESSION:
Once it's feeling pretty easy, explore it on one foot — start by narrowing your stance more and more to invite a moderate amount of instability.`,
      },
    ],
  },
  {
    id: 'fascia-work',
    title: 'Fascia Work',
    tagline: 'Release, rehydrate, and restore fluid movement.',
    accent: '#8a6a5a',
    accentBg: 'bg-[#f1e8dd]',
    learnMore: { label: 'Learn more about fascia & the lines', to: '/learn/fascia' },
    videos: [
      {
        title: 'Foam Roller',
        duration: '',
        description: "Here's a standard full foam roller routine to keep you moving, get blood flowing, and recover or warm up.",
        videoUrl: 'https://youtu.be/tUz6cKR_Mvo',
      },
      {
        title: 'From the Ground Up',
        duration: '',
        description: "From the Ground Up is a great session — no big movements, touching the fascia from feet to top of head. Will free your body and gain clarity of working with fascia. This video contains more instruction. See the next video for the Cheat Sheet.\n\nIncludes: Vagus Nerve (2 ways), Ankles, Achilles, Knees (front/back), Psoas, Elbow Torque, Ear Pull, Ear Twist.",
        videoUrl: 'https://youtu.be/dp-Ba6CAndw',
      },
      {
        title: 'From the Ground Up — Cheat Sheet',
        duration: '',
        description: 'Quick-reference version — brief descriptions only, same flow.',
        videoUrl: 'https://youtu.be/9_Bo2mTGwTQ',
      },
      {
        title: 'Fascial Coffee Practice',
        duration: '',
        description: 'A gentle practice to learn and feel more about your fascia — tuning in to the connective web that shapes and moves your whole body.',
        videoUrl: 'https://youtu.be/jAS8KmPd0Ls',
      },
      {
        title: 'Intro to Stress Reset',
        duration: '',
        description: 'An introduction to the Stress Reset practice — what it is, how it works, and how to use it to downshift and release.',
        videoUrl: 'https://youtu.be/IB10DIqV_b0',
      },
      {
        title: 'Stress Reset — Totally Twisted, Anti-Gravity, Elbow Torque, Forehead Release',
        duration: '',
        description: 'The full Stress Reset sequence: Totally Twisted, Anti-Gravity, Elbow Torque, and Forehead Release.',
        videoUrl: 'https://youtu.be/c-8RuEgU3Fw',
      },
      {
        title: 'Stress Reset — Short & Assertive',
        duration: '',
        description: 'A shorter, more assertive Stress Reset — with an extended focus on Totally Twisted.',
        videoUrl: 'https://youtu.be/2H5G_xddmTQ',
      },
      {
        title: 'Ear / Sinus / Forehead / Neck Work',
        duration: '',
        description: 'Targeted fascial release for the ears, sinuses, forehead and neck — easing tension and encouraging drainage and flow through the head and neck.',
        videoUrl: 'https://youtu.be/2amFK20v3gA',
      },
      {
        title: 'Fascial Face Work',
        duration: '',
        description: 'Gentle fascial work for the face — release tension, encourage circulation, and bring softness and life back to the facial tissues.',
        videoUrl: 'https://youtu.be/n5Sw-jwMhqY',
      },
      {
        title: 'Fascia & How to Program the Body | Keynote from a Founder of Human Garage',
        duration: '',
        description: 'Want to dive a little deeper into the power of fascia? Watch Jason and experience this talk.',
        videoUrl: 'https://youtu.be/f253Dr7Lys4',
      },
    ],
  },
  {
    id: 'functional-fitness',
    title: 'Functional Fitness',
    tagline: 'Real-life strength — move well, live well.',
    accent: '#4a7a7a',
    accentBg: 'bg-[#e4efee]',
    videos: [
      { title: 'Placeholder Video 1', duration: '', description: 'Coming soon.' },
      { title: 'Placeholder Video 2', duration: '', description: 'Coming soon.' },
      { title: 'Placeholder Video 3', duration: '', description: 'Coming soon.' },
    ],
  },
  {
    id: 'neuro-movements',
    title: 'Neuro Movements',
    tagline: 'Gentle, brain-based movements to calm the nervous system and re-pattern.',
    accent: '#5a4a7a',
    accentBg: 'bg-[#eae4f0]',
    videos: [
      {
        title: 'Repatterning',
        duration: '',
        videoUrl: 'https://youtu.be/Mgu-5baN9v0',
        description: 'For calming mind & body — a great "brain gym" to balance a busy mind and an out-of-balance body, mood & emotions. Particularly beneficial for depression, anxiety, ADHD — and really, everything.',
        content: `Repatterning takes a total of three minutes. I encourage clients to repattern every morning and again before they go to bed.

STEP 1 — Cross crawl + hum (1 minute)
Lie on your back. Put your arms over your head. Look up to the left to access the right side of your brain. Cross crawl by bringing your opposite hand to your opposite knee. Hum in a monotone to access the auditory centers of the brain. (Do not use a tune — tunes access the left hemisphere.) Continue for one minute.

STEP 2 — Homolateral crawl + count back (1 minute)
Continue to lie on your back. Look down to your right to access the left hemisphere. Bring your arms by your side. Begin homolateral crawling by bending the right knee to the chest and lifting the right arm straight over your head. Repeat with the opposite side. As you do the homolateral crawl, simultaneously count backwards from thirty to access the logical, linear part of the brain.

STEP 3 — Silent cross crawl + eye circles (1 minute)
Without making a sound, continue to lie on your back and cross crawl. Move your eyes in a big circle. As you look up, you access the visual centers; as you look down, the kinesthetic senses; as you look left and right, the auditory portions of the brain. Continue for one minute.`,
      },
      { title: 'Neuro Movements — Coming Soon', duration: '', description: 'Coming soon.' },
      { title: 'Neuro Movements — Coming Soon', duration: '', description: 'Coming soon.' },
    ],
  },
];

// Top-level tabs — group sub-sections under one tab when needed.
// To add another sub-section under any tab, add the section id to its sectionIds array.
const tabs = [
  {
    id: 'postural-development',
    title: 'Postural Development',
    sectionIds: ['postural-development', 'putting-it-together', 'multifidus-ql'],
  },
  { id: 'restorative', title: 'Restorative', sectionIds: ['restorative'] },
  { id: 'stability-ball', title: 'Stability Ball', sectionIds: ['stability-ball'] },
  { id: 'fascia-work', title: 'Fascia Work', sectionIds: ['fascia-work'] },
  { id: 'functional-fitness', title: 'Functional Fitness', sectionIds: ['functional-fitness'] },
  { id: 'natural-movement', title: 'Natural Movement', sectionIds: ['natural-movement'] },
  { id: 'neuro-movements', title: 'Neuro Movements', sectionIds: ['neuro-movements'] },
];

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
  return m ? m[1] : null;
};

const hay = (...parts) => parts.filter(Boolean).join(' ').toLowerCase();

// Static search index: library videos + Stability Ball / Pattern videos
const staticSearchItems = [
  ...categories.filter((cat) => !['stability-ball', 'functional-fitness'].includes(cat.id)).flatMap((cat) => {
    const tabTitle = tabs.find((t) => t.sectionIds.includes(cat.id))?.title || cat.title;
    const where = tabTitle === cat.title ? cat.title : `${tabTitle} · ${cat.title}`;
    return cat.videos.map((vid, idx) => {
      const ytId = getYouTubeId(vid.videoUrl);
      const quickId = getYouTubeId(vid.quickUrl);
      if (!ytId && !quickId && !vid.content) return null;
      return {
        key: `vid-${cat.id}-${idx}`, kind: 'video', title: vid.title, where, tabId: tabs.find((t) => t.sectionIds.includes(cat.id))?.id,
        thumb: (ytId || quickId) ? `https://img.youtube.com/vi/${ytId || quickId}/hqdefault.jpg` : null,
        payload: { ...vid, ytId, quickId, dualVideo: cat.dualVideo },
        haystack: hay(vid.title, vid.description, vid.content, where),
      };
    }).filter(Boolean);
  }),
  ...ballSections.flatMap((section) => section.cards.flatMap((card) => (card.videos || []).map((v, i) => {
    const where = `${section.id === 'stretches' ? 'Stability Ball' : 'Functional Fitness'} · ${card.title}`;
    return {
      key: `ball-${card.id}-${i}`, kind: 'video', title: v.title, where,
      thumb: `https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`,
      payload: { title: v.title, description: v.blurb || '', ytId: v.youtubeId },
      haystack: hay(v.title, v.blurb, where),
    };
  }))),
];

const useVipSearch = () => {
  const [query, setQuery] = useState('');
  const [movements, setMovements] = useState([]);
  useEffect(() => {
    fetch(`${API}/vip/movements`).then((r) => (r.ok ? r.json() : { movements: [] }))
      .then((d) => setMovements(d.movements || [])).catch(() => {});
  }, []);
  const items = useMemo(() => [
    ...staticSearchItems,
    ...movements.map((m) => ({
      key: `mov-${m.id}`, kind: 'movement', title: m.title, where: 'Functional Fitness · My Movements', id: m.id,
      thumb: `${API}/vip/movements/${m.id}/file`,
      haystack: hay(m.title, m.description, m.modifications, 'my movements functional fitness'),
    })),
  ], [movements]);
  const results = useMemo(() => matchItems(items, query), [items, query]);
  return { query, setQuery, results };
};

const VIPPage = () => {
  const { user, hasAccess, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [activeVideo, setActiveVideo] = useState(null);
  const [videoMode, setVideoMode] = useState('tutorial'); // 'tutorial' | 'quick'
  const [openMovementId, setOpenMovementId] = useState(null);
  const clearOpenMovement = useCallback(() => setOpenMovementId(null), []);
  const { query, setQuery, results } = useVipSearch();
  const searching = query.trim().length > 0;

  const pickResult = (it) => {
    if (it.kind === 'movement') {
      setQuery('');
      setActiveTab('functional-fitness');
      setOpenMovementId(it.id);
    } else {
      setVideoMode('tutorial');
      setActiveVideo(it.payload);
    }
  };

  // Lock background scrolling while the video modal is open
  useEffect(() => {
    document.body.style.overflow = activeVideo ? 'hidden' : '';
    const onKey = (e) => e.key === 'Escape' && setActiveVideo(null);
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [activeVideo]);

  // Pull fresh user data from server on mount so newly-granted access takes effect
  useEffect(() => {
    if (user?.id) {
      refreshUser(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-500 font-body">Loading…</div>
      </div>
    );
  }

  // Access gate
  if (!user || !hasAccess('vip')) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="vip-access-gate">
        <div className="max-w-2xl mx-auto text-center">
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-10"
            data-testid="vip-back-to-programs"
          >
            <ArrowLeft size={16} /> Back to Programs
          </Link>

          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-6">
              <Crown size={28} />
            </div>
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3">
              Private Members Area
            </p>
            <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">
              V.I.P. Video Library
            </h1>
            <p className="text-stone-600 font-body leading-relaxed mb-8">
              A private space for Jill's in-person clients — a growing library of common
              healing routines across Postural Development, Restorative, Fascia Work, Functional Fitness, and
              Natural Movement.
            </p>
            <p className="text-sm text-stone-500 font-body mb-8">
              Already a V.I.P. client?{' '}
              {!user ? (
                <button
                  onClick={() => navigate('/programs')}
                  className="text-forest underline hover:text-forest-dark"
                  data-testid="vip-login-link"
                >
                  Log in from the Programs page
                </button>
              ) : (
                <span>
                  Your account doesn't have V.I.P. access yet. Please reach out to Jill to be added.
                </span>
              )}
            </p>
            <a
              href="mailto:heyjbennett@yahoo.ca?subject=V.I.P. Membership Inquiry"
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="vip-inquiry-link"
            >
              Inquire about V.I.P. <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="vip-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8"
        >
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        <VipSwitcher active="video" />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-2 mb-3">
            <Crown size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
              V.I.P. · Private Members Area
            </p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-3">
            Your V.I.P. Video Library
          </h1>
          <p className="text-stone-600 font-body max-w-2xl">
            Welcome back{user.name ? `, ${user.name.split(' ')[0]}` : ''}. A growing library
            of the routines we work through together — ready whenever you are.
          </p>
        </motion.div>

        {/* Where to go — friendly direction cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="vip-quicklinks">
          {[
            { to: '/programs/vip/start-here', icon: Flag, title: 'New here? Start Here', text: "Finish your intake forms so we're ready to begin." },
            { to: '/programs/vip/journal', icon: NotebookPen, title: 'Your Plan & Notes', text: 'See the exercises Jill picked for you — and jot your own notes.' },
            { to: '/programs/vip/jills-corner', icon: Sparkles, title: "Jill's Corner", text: 'Your space for the work we do together, session by session.' },
          ].map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="group bg-white border border-stone-200 rounded-2xl p-5 hover:border-[#8a6a5a]/50 hover:shadow-md transition-all flex flex-col"
              data-testid={`vip-quicklink-${q.to.split('/').pop()}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#f3ece0] flex items-center justify-center mb-3">
                <q.icon size={18} className="text-[#8a6a5a]" />
              </div>
              <p className="font-heading text-lg text-forest mb-1">{q.title}</p>
              <p className="font-body text-sm text-stone-500 leading-relaxed flex-1">{q.text}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-body text-[#8a6a5a] mt-3 group-hover:gap-2.5 transition-all">
                Go there <ArrowRight size={15} />
              </span>
            </Link>
          ))}
        </div>

        {/* Reading Room — its own resources section */}
        <Link
          to="/programs/vip/reading-room"
          className="group block bg-[#f5efe4] border border-stone-200 rounded-2xl p-6 sm:p-7 mb-12 hover:border-[#8a6a5a]/50 hover:shadow-md transition-all"
          data-testid="vip-reading-room-card"
        >
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <BookOpen size={26} className="text-[#8a6a5a]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-xl sm:text-2xl text-forest mb-1">The Reading Room</p>
              <p className="font-body text-sm text-stone-500 leading-relaxed">
                A private library of readings & resources — open any title and read it right here.
              </p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-body text-[#8a6a5a] group-hover:gap-2.5 transition-all flex-shrink-0">
              Enter <ArrowRight size={15} />
            </span>
          </div>
        </Link>

        <VipSearchBar value={query} onChange={setQuery} />
        <SearchChips words={SEARCH_CHIPS} active={query} onPick={setQuery} />

        {searching ? (
          <VipSearchResults query={query} results={results} onPick={pickResult} />
        ) : (
        <>
        {/* Category tabs */}
        <div
          className="flex flex-wrap gap-2 mb-8 pb-2 border-b border-stone-200"
          data-testid="vip-category-tabs"
        >
          {tabs.map((t) => {
            const isActive = t.id === activeTab;
            return (
              <button
                key={t.id}
                onClick={() => { setActiveTab(t.id); setActiveVideo(null); }}
                className={`px-5 py-2.5 rounded-full font-body text-sm transition-colors ${
                  isActive
                    ? 'bg-forest text-cream'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-forest/40'
                }`}
                data-testid={`vip-tab-${t.id}`}
              >
                {t.title}
              </button>
            );
          })}
        </div>

        {/* Active tab's sub-sections, stacked */}
        <div className="space-y-12" data-testid="vip-categories">
          {activeTab === 'stability-ball' ? (
            <StabilityBallProgressions only="stretches" />
          ) : activeTab === 'functional-fitness' ? (
            <>
              <MyMovements openId={openMovementId} onOpened={clearOpenMovement} />
              <StabilityBallProgressions only="strength" />
            </>
          ) : (
          (tabs.find((t) => t.id === activeTab)?.sectionIds || [])
            .map((sid) => categories.find((c) => c.id === sid))
            .filter(Boolean)
            .map((cat) => (
            <section key={cat.id} data-testid={`vip-section-${cat.id}`}>
              <div
                className={`rounded-2xl border border-stone-200 ${cat.accentBg} p-6 md:p-8 mb-6`}
                data-testid={`vip-category-header-${cat.id}`}
              >
                <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">
                  {cat.title}
                </h2>
                <p className="text-stone-600 font-body italic">{cat.tagline}</p>
                {cat.learnMore && (
                  <Link
                    to={cat.learnMore.to}
                    className="inline-flex items-center gap-1.5 mt-3 font-body text-sm text-forest hover:text-forest-dark underline underline-offset-4 decoration-forest/40"
                    data-testid={`vip-learnmore-${cat.id}`}
                  >
                    {cat.learnMore.label} <ChevronRight size={15} />
                  </Link>
                )}
              </div>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid={`vip-video-grid-${cat.id}`}
              >
                {cat.videos.map((vid, idx) => {
                  const ytId = getYouTubeId(vid.videoUrl);
                  const quickId = getYouTubeId(vid.quickUrl);
                  const hasVideo = Boolean(ytId || quickId);
                  const thumbId = ytId || quickId;
                  const openable = hasVideo || Boolean(vid.content);
                  return (
                    <motion.button
                      key={idx}
                      type="button"
                      onClick={() => openable && (setVideoMode('tutorial'), setActiveVideo({ ...vid, ytId, quickId, dualVideo: cat.dualVideo }))}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.05 }}
                      className={`text-left bg-white rounded-xl border border-stone-200 overflow-hidden transition-shadow ${
                        openable ? 'hover:shadow-lg cursor-pointer' : 'opacity-80 cursor-default'
                      }`}
                      data-testid={`vip-video-card-${cat.id}-${idx}`}
                    >
                      <div
                        className={`relative aspect-video ${cat.accentBg} flex items-center justify-center`}
                      >
                        {hasVideo ? (
                          <>
                            <img
                              src={`https://img.youtube.com/vi/${thumbId}/hqdefault.jpg`}
                              alt={vid.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-forest/30 flex items-center justify-center">
                              <div className="w-14 h-14 rounded-full bg-cream/90 flex items-center justify-center">
                                <Play size={22} className="text-forest ml-0.5" />
                              </div>
                            </div>
                            {cat.dualVideo && (
                              <span className="absolute top-2 right-2 bg-cream/95 text-forest text-[10px] font-body font-medium px-2 py-1 rounded-full shadow-sm">
                                Tutorial{quickId ? ' + Quick' : ' · Quick soon'}
                              </span>
                            )}
                          </>
                        ) : vid.content ? (
                          <div className="text-center text-forest font-body px-4">
                            <FileText size={22} className="mx-auto mb-2 text-terracotta" />
                            <p className="text-xs uppercase tracking-[0.2em]">Read the steps</p>
                            <p className="text-[11px] text-stone-400 mt-1">Video coming soon</p>
                          </div>
                        ) : (
                          <div className="text-center text-stone-400 font-body">
                            <Sparkles size={22} className="mx-auto mb-2 opacity-50" />
                            <p className="text-xs uppercase tracking-[0.2em]">Video coming soon</p>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-base text-forest leading-snug mb-1">
                          {vid.title}
                        </h3>
                        {vid.duration && (
                          <p className="text-xs text-stone-400 font-body mb-1">{vid.duration}</p>
                        )}
                        <p className="text-sm text-stone-500 font-body line-clamp-2">
                          {vid.description}
                        </p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </section>
          )))}
        </div>
        </>
        )}

        {/* Video Modal */}
        <AnimatePresence>
          {activeVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setActiveVideo(null)}
              data-testid="vip-video-modal"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setActiveVideo(null)}
                  className="absolute top-2 right-2 z-20 w-9 h-9 rounded-full bg-white/90 text-forest shadow flex items-center justify-center hover:bg-white"
                  aria-label="Close"
                  data-testid="vip-video-modal-close"
                >
                  <X size={18} />
                </button>
                {(() => {
                  const currentId = videoMode === 'quick' ? activeVideo.quickId : activeVideo.ytId;
                  return (
                    <>
                      {activeVideo.dualVideo && (
                        <div className="sticky top-0 z-10 flex items-center gap-2 p-3 bg-stone-50 border-b border-stone-200">
                          <button
                            onClick={() => setVideoMode('tutorial')}
                            className={`px-4 py-1.5 rounded-full font-body text-sm transition-colors ${videoMode === 'tutorial' ? 'bg-forest text-cream' : 'text-stone-600 hover:bg-stone-200'}`}
                            data-testid="vip-video-tab-tutorial"
                          >
                            📺 Full Tutorial
                          </button>
                          <button
                            onClick={() => setVideoMode('quick')}
                            className={`px-4 py-1.5 rounded-full font-body text-sm transition-colors ${videoMode === 'quick' ? 'bg-forest text-cream' : 'text-stone-600 hover:bg-stone-200'}`}
                            data-testid="vip-video-tab-quick"
                          >
                            ⚡ Quick Version
                          </button>
                        </div>
                      )}
                      <div className="aspect-video bg-black flex items-center justify-center">
                        {currentId ? (
                          <iframe
                            key={currentId}
                            src={`https://www.youtube.com/embed/${currentId}?rel=0&playsinline=1`}
                            title={activeVideo.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                          />
                        ) : (
                          <div className="text-center text-cream/90 font-body px-6" data-testid="vip-video-quick-comingsoon">
                            <Sparkles size={26} className="mx-auto mb-3 opacity-70" />
                            <p className="font-heading text-lg mb-1">{activeVideo.content ? 'Video coming soon' : 'Quick version coming soon'}</p>
                            <p className="text-sm text-cream/70">{activeVideo.content ? 'Jill will post the video here soon — the full steps are just below.' : 'Jill is filming the short version of this one — check back soon. In the meantime, the full tutorial has everything you need.'}</p>
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading text-xl text-forest mb-1">
                          {activeVideo.title}
                        </h3>
                        <p className="text-sm text-stone-500 font-body whitespace-pre-line">{activeVideo.description}</p>
                        {activeVideo.content && (
                          <div className="mt-4 border-t border-stone-100 pt-4 font-body text-sm text-stone-700 leading-relaxed whitespace-pre-line" data-testid="vip-video-content">
                            {activeVideo.content}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VIPPage;
